/**
 * API client. The access token lives in memory only; the refresh token is an
 * httpOnly cookie the browser sends by itself. A 401 triggers one silent
 * refresh, and concurrent callers share it.
 */
export interface ApiErrorShape {
  message: string | string[];
  error?: string;
  statusCode: number;
}

export class ApiError extends Error {
  readonly status: number;
  /** Field messages from the 422 validation pipe, ready to show inline. */
  readonly fieldMessages: string[];

  constructor(status: number, message: string, fieldMessages: string[] = []) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.fieldMessages = fieldMessages;
  }
}

let accessToken: string | null = null;
let onUnauthenticated: (() => void) | null = null;
let refreshing: Promise<boolean> | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function setUnauthenticatedHandler(handler: (() => void) | null): void {
  onUnauthenticated = handler;
}

async function refreshAccessToken(): Promise<boolean> {
  refreshing ??= (async () => {
    try {
      const response = await fetch('/api/v1/auth/refresh', {
        method: 'POST',
        credentials: 'include',
      });
      if (!response.ok) return false;
      const body = (await response.json()) as { accessToken: string };
      accessToken = body.accessToken;
      return true;
    } catch {
      return false;
    } finally {
      // Cleared once settled, so callers that 401 during the refresh share it
      // rather than each starting another with the same cookie.
      queueMicrotask(() => (refreshing = null));
    }
  })();
  return refreshing;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /** Skip the silent refresh, e.g. for the login call itself. */
  anonymous?: boolean;
}

/**
 * A fetch with the access token and the one silent refresh on 401: what every
 * call to the API goes through, JSON or not (file downloads use it too).
 */
export async function apiFetch(path: string, options: RequestOptions = {}): Promise<Response> {
  const send = (): Promise<Response> =>
    fetch(`/api/v1${path}`, {
      method: options.method ?? 'GET',
      credentials: 'include',
      signal: options.signal,
      headers: {
        ...(options.body === undefined ? {} : { 'content-type': 'application/json' }),
        ...(accessToken && !options.anonymous ? { authorization: `Bearer ${accessToken}` } : {}),
      },
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
    });

  let response = await send();

  if (response.status === 401 && !options.anonymous) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      response = await send();
    } else {
      accessToken = null;
      onUnauthenticated?.();
    }
  }
  return response;
}

/** Downloads a file from the API (Excel export, a stored document) and saves it as `filename`. */
export async function apiDownload(path: string, filename: string): Promise<void> {
  const response = await apiFetch(path);
  if (!response.ok) {
    let message = `Download failed (${response.status})`;
    try {
      const shape = (await response.json()) as ApiErrorShape;
      const first = Array.isArray(shape.message) ? shape.message[0] : shape.message;
      if (first) message = first;
    } catch {
      // Not JSON: keep the status message.
    }
    throw new ApiError(response.status, message);
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await apiFetch(path, options);

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload = text ? (JSON.parse(text) as unknown) : null;

  if (!response.ok) {
    const shape = payload as ApiErrorShape | null;
    const messages = Array.isArray(shape?.message)
      ? shape.message
      : shape?.message
        ? [shape.message]
        : [`Request failed (${response.status})`];
    throw new ApiError(response.status, messages[0], messages);
  }

  return payload as T;
}
